import { useRef } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SrIcon } from "@/components/ui/SrIcon";
import { useUploadAvatar } from "@/hooks/use-avatar-upload";
import type { DBUser } from "@/lib/db-types";
import { formatDate } from "@/lib/format";
import { formatPhoneDisplay } from "@/lib/phone-format";
import { ROLE_COLORS, SPECIALIZATION_COLORS, SPECIALIZATION_LABELS, SYSTEM_ROLE_LABELS } from "@/lib/constants";
import { cn } from "@/lib/utils";

interface EmployeeHeaderProps {
  user: DBUser;
  /** VIB-998: bare admin kan bytte profilbilde (sentralt styrt i Ansatt-modulen). */
  canChangeAvatar: boolean;
}

const FALLBACK_PILL = "bg-secondary text-foreground";

export function EmployeeHeader({ user, canChangeAvatar }: EmployeeHeaderProps) {
  const uploadAvatar = useUploadAvatar();
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const initials = user.full_name.split(" ").map((n) => n[0]).join("").slice(0, 2);
  const subtitle = [user.job_title, user.hire_date && `ansatt siden ${formatDate(user.hire_date)}`]
    .filter(Boolean)
    .join(" · ");

  return (
    <Card>
      <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between sm:px-6">
        <div className="flex min-w-0 items-start gap-4">
          <div className="relative h-16 w-16 shrink-0">
            {user.avatar_url ? (
              <img src={user.avatar_url} alt={user.full_name} className="h-16 w-16 rounded-full object-cover" />
            ) : (
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-lg font-medium text-primary">
                {initials}
              </span>
            )}
            {canChangeAvatar && (
              <>
                <button
                  type="button"
                  onClick={() => avatarInputRef.current?.click()}
                  disabled={uploadAvatar.isPending}
                  className="absolute inset-0 flex items-center justify-center rounded-full bg-black/40 text-white opacity-0 transition-opacity hover:opacity-100 focus-visible:opacity-100 disabled:opacity-100"
                  aria-label="Bytt profilbilde"
                  title="Bytt profilbilde"
                >
                  <SrIcon
                    name={uploadAvatar.isPending ? "loader2" : "upload"}
                    className={cn("h-5 w-5", uploadAvatar.isPending && "animate-spin")}
                  />
                </button>
                <input
                  ref={avatarInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  aria-hidden="true"
                  tabIndex={-1}
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) uploadAvatar.mutate({ userId: user.id, file: f });
                    e.target.value = "";
                  }}
                />
              </>
            )}
          </div>
          <div className="flex min-w-0 flex-col gap-1.5">
            <h1 className="font-display text-[clamp(24px,3vw,32px)] font-semibold leading-tight tracking-[-0.01em] text-foreground">
              {user.full_name}
            </h1>
            {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
            <div className="flex flex-wrap items-center gap-1.5">
              <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-medium", ROLE_COLORS[user.role] ?? FALLBACK_PILL)}>
                {SYSTEM_ROLE_LABELS[user.role] ?? user.role}
              </span>
              {user.specialization && (
                <span
                  className={cn(
                    "rounded-full px-2.5 py-0.5 text-xs font-medium",
                    SPECIALIZATION_COLORS[user.specialization] ?? FALLBACK_PILL,
                  )}
                >
                  {SPECIALIZATION_LABELS[user.specialization] ?? user.specialization}
                </span>
              )}
              {!user.is_active && <Badge variant="secondary">Inaktiv</Badge>}
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-1 text-sm sm:items-end">
          <span className="flex items-center gap-1.5">
            <SrIcon name="mail" className="h-3.5 w-3.5 text-muted-foreground" />
            {user.email}
          </span>
          {user.phone && (
            <span className="flex items-center gap-1.5 tabular-nums">
              <SrIcon name="phone" className="h-3.5 w-3.5 text-muted-foreground" />
              {formatPhoneDisplay(user.phone)}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
